import { Png, Svg } from "@/assets";
import { ContentPage } from "./ContentPage";
import { Media } from "./Media";
import { Player } from "./UserAccount";
import { Post } from "./Post";
import { SmartLink } from "@/services/bedrock";

export interface LiquidVariable {
  name: string;
  dataType: "liquid_drop" | "string";
  value: LiquidVariable[] | string | null;
}

export interface LiquidDrop {
  liquidVariables: LiquidVariable[];
}

export type PolymorphicPlayerCard = PlayerCard | PlayerCardTemplate;
export type PolymorphicPlayerCardWidget = PlayerCardWidget | PlayerCardTemplateWidget;

export interface PlayerCard extends LiquidDrop {
  id: number;
  createdDate: string;
  isTemplate: false;
  slug: string;
  baseUrl: string;
  playerId: number;
  player: Player;
  themeId: number;
  theme: PlayerCardTheme | null;
  defaultTheme: PlayerCardTheme | null;
  templateId: number;
  template: PlayerCardTemplate | null;
  defaultTemplate: PlayerCardTemplate | null;
  isPubliclyAvailable: boolean;
  viewMetricsCount: number;
  clickMetricsCount: number;
  widgets: PlayerCardWidget[];
  // PlayerCardTemplate properties that shouldn't exist in PlayerCard
  name?: never;
  coachId?: never;
}

export interface PlayerCardTemplate extends PlayerCardDefaultable {
  isTemplate: true;
  coachId: number;
  widgets: PlayerCardTemplateWidget[];
  // PlayerCard properties that shouldn't exist in PlayerCardTemplate
  slug?: never;
  url?: never;
  playerId?: never;
  player?: never;
  themeId?: never;
  theme?: never;
  templateId?: never;
  template?: never;
  isPubliclyAvailable?: never;
  viewMetricsCount?: never;
  clickMetricsCount?: never;
}

interface PlayerCardWidgetBase {
  id: number;
  isVisible: boolean;
  sortIndex: number | null;
  owner: PlayerCardWidgetOwner;
}

export interface PlayerCardWidget extends PlayerCardWidgetBase {
  clickMetricsCount: number;
  // PlayerCardWidgetTemplate properties that shouldn't exist in PlayerCardWidget
  templateFields?: never;
}

export interface PlayerCardTemplateWidget extends PlayerCardWidgetBase {
  templateFields: PlayerCardTemplateField[];
  // PlayerCardWidget properties that shouldn't exist in PlayerCardWidgetTemplate
  clickMetricsCount?: never;
}

export type PlayerCardWidgetOwner =
  | PlayerCardHeader
  | PlayerCardFooter
  | PlayerCardLink
  | PlayerCardSocialMedia
  | PlayerCardContentPage
  | PlayerCardPost
  | PlayerCardSmartLink
  | PlayerCardPhone
  | PlayerCardAddress
  | PlayerCardEmail
  | PlayerCardContact;

interface PlayerCardHeader {
  id: number;
  type: "Header";
  bannerMedia: Media[];
  profileMedia: Media[];
  useProfilePhotoUrl: boolean;
  profilePhotoUrl?: string;
  userDisplayName?: string;
  userTitle?: string;
  userSubtitle?: string;
  userBio?: string;
}

interface PlayerCardFooter {
  id: number;
  type: "Footer";
  disclaimer?: string;
  logosMedia: Media[];
}

interface PlayerCardLink {
  id: number;
  type: "Link";
  thumbnailMedia: Media[];
  title: string;
  url: string;
}

interface PlayerCardSocialMedia {
  id: number;
  type: "SocialMedia";
  displayFacebook: boolean;
  displayInstagram: boolean;
  displayLinkedIn: boolean;
  displayX: boolean;
  displayTikTok: boolean;
  displayGoogle: boolean;
  displayZillow: boolean;
}

interface PlayerCardContentPage {
  id: number;
  type: "ContentPage";
  selectionType: "SPECIFIC" | "RANDOM" | "MOST_RECENT";
  contentPageId: number | null;
  contentPage?: ContentPage | null;
  title: string | null;
}

interface PlayerCardPost {
  id: number;
  type: "Post";
  selectionType: "SPECIFIC" | "RANDOM" | "MOST_RECENT";
  postId: number | null;
  post?: Post | null;
  title: string | null;
}

interface PlayerCardSmartLink {
  id: number;
  type: "SmartLink";
  selectionType: "SPECIFIC" | "RANDOM" | "MOST_RECENT";
  styleType: "BUTTON" | "CARD";
  smartLinkId: number | null;
  smartLink?: SmartLink | null;
  title: string | null;
}

interface PlayerCardPhone {
  id: number;
  type: "Phone";
  phoneType: string | null;
  displayPhoneType: boolean;
  phoneNumber: string;
  title: string | null;
}

interface PlayerCardEmail {
  id: number;
  type: "Email";
  emailType: string | null;
  displayEmailType: boolean;
  email: string;
  title: string | null;
}

interface PlayerCardAddress {
  id: number;
  type: "Address";
  addressType: string | null;
  displayAddressType: boolean;
  streetLine1: string;
  streetLine2: string | null;
  city: string;
  state: string;
  zip: string;
  country: string;
  title: string | null;
}

interface PlayerCardContact {
  id: number;
  type: "Contact";
  title: string | null;
}

export interface PlayerCardDefaultable {
  id: number;
  name: string;
}
export const PlayerCardDefaultables: { [key: string]: PlayerCardDefaultable } = {
  default: {
    id: 0,
    name: "Default",
  },
  unassigned: {
    id: -1,
    name: "Unassigned",
  },
};

export interface PlayerCardDefault {
  id: number;
  defaultId: number;
  segmentationDetailId: number | null;
}

export interface PlayerCardTheme extends PlayerCardDefaultable {
  coachId: number | null;
  playerId: number | null;
  player: Player | null;
  pageBackground: string;
  pageForeground: string;
  widgetBackground: string;
  widgetForeground: string;
}

export interface PlayerCardThemeDefault extends PlayerCardDefault {
  themeId: number;
}

interface PlayerCardTemplateField {
  id: number;
  fieldName: string;
  isLocked: boolean;
}

export interface PlayerCardTemplateDefault extends PlayerCardDefault {
  templateId: number;
}

export interface PlayerCardMetric {
  id: number;
  ipAddress: IpAddress;
  userAgent: UserAgent;
  playerCardId: number;
  playerCardWidgetId: number | null;
  sessionGuid: string;
  metricType: "VIEW" | "CLICK";
  tags: string;
  createdDate: string;
  widgetType: string | null;
}

export interface PlayerCardMetricSummaries {
  total: number;
  dailySummary: {
    year: number;
    month: number;
    day: number;
    total: number;
  }[];
  ipAddressSummary: {
    city: string;
    state: string;
    country: string;
    total: number;
  }[];
  userAgentSummary: {
    agentClass: string;
    agentName: string;
    deviceClass: string;
    deviceName: string;
    osClass: string;
    osName: string;
    total: number;
  }[];
}

interface IpAddress {
  id: number;
  city: string;
  state: string;
  country: string;
  zip: string;
}

interface UserAgent {
  id: number;
  agentClass: string;
  agentName: string;
  deviceClass: string;
  deviceName: string;
  osClass: string;
  osName: string;
}

export const dummyPlayerCard: PlayerCard = {
  id: -1,
  createdDate: "",
  isTemplate: false,
  slug: "slug",
  baseUrl: "url",
  playerId: -1,
  player: {
    id: -1,
    fullName: "full name",
    firstName: "full",
    lastName: "name",
    email: "email",
    userId: 0,
    phone: null,
    photoUrl: null,
  },
  themeId: 0,
  theme: null,
  defaultTheme: null,
  templateId: 0,
  template: null,
  defaultTemplate: null,
  isPubliclyAvailable: false,
  viewMetricsCount: 0,
  clickMetricsCount: 0,
  widgets: [
    {
      id: -1,
      isVisible: true,
      sortIndex: 0,
      clickMetricsCount: 0,
      owner: {
        id: -1,
        type: "Header",
        bannerMedia: [
          {
            id: -1,
            sortOrder: 1,
            category: "IMAGE",
            uri: Png.dummyBanner,
            type: "image/jpeg",
            extension: "jpeg",
          },
        ],
        profileMedia: [
          {
            id: -1,
            sortOrder: 1,
            category: "IMAGE",
            uri: Svg.noImageuser,
            type: "image/jpeg",
            extension: "jpeg",
          },
        ],
        useProfilePhotoUrl: true,
        userDisplayName: "Lorem ipsum",
        userTitle: "Dolor sit amet",
        userSubtitle: "Consectetur adipiscing elit",
        userBio:
          "Lorem ipsum dolor sit amet consectetur adipiscing elit. Quisque faucibus ex sapien vitae pellentesque sem placerat.",
      },
    },
    {
      id: -2,
      isVisible: true,
      sortIndex: 1,
      clickMetricsCount: 0,
      owner: {
        id: -1,
        type: "Link",
        thumbnailMedia: [],
        title: "Lorem ipsum",
        url: "https://example.com",
      },
    },
    {
      id: -3,
      isVisible: true,
      sortIndex: 2,
      clickMetricsCount: 0,
      owner: {
        id: -1,
        type: "Footer",
        disclaimer:
          "Lorem ipsum dolor sit amet consectetur adipiscing elit. Quisque faucibus ex sapien vitae pellentesque sem placerat. In id cursus mi pretium tellus duis convallis. Tempus leo eu aenean sed diam urna tempor. Pulvinar vivamus fringilla lacus nec metus bibendum egestas. Iaculis massa nisl malesuada lacinia integer nunc posuere. Ut hendrerit semper vel class aptent taciti sociosqu. Ad litora torquent per conubia nostra inceptos himenaeos.",
        logosMedia: [],
      },
    },
  ],
  liquidVariables: [
    {
      name: "player",
      dataType: "liquid_drop",
      value: [
        {
          name: "first_name",
          dataType: "string",
          value: "Alex",
        },
        {
          name: "last_name",
          dataType: "string",
          value: "Smith",
        },
        {
          name: "full_name",
          dataType: "string",
          value: "Alex Smith",
        },
        {
          name: "email",
          dataType: "string",
          value: "alexsmith@me.com",
        },
        {
          name: "internal_identifier",
          dataType: "string",
          value: "12345",
        },
        {
          name: "compliance_disclaimer",
          dataType: "string",
          value: "NMLS #1234",
        },
        {
          name: "title",
          dataType: "string",
          value: "Loan Officer",
        },
        {
          name: "industry_role",
          dataType: "string",
          value: "Loan Officer",
        },
        {
          name: "company_name",
          dataType: "string",
          value: "Acme Corp",
        },
        {
          name: "web_address",
          dataType: "string",
          value: "https://socialcoach.io",
        },
      ],
    },
  ],
};
